// Resolves one Materials doc to the interventie it represents, plus how many
// registration units one of it is worth.
//
// NEVER THROWS. This runs behind the Materials page and the per-household
// materials table, both of which save per field. A helper that threw on
// incomplete input would lose the edit that was being made — a failure this
// codebase has shipped twice.
// AI-CONTEXT: docs/pitfalls.md#ui-strict-validator-kills-autosave
//
// Instead of failing it reports a status, so the UI can say precisely what is
// missing rather than showing a bare dash:
//
//   'ok'           — a number can be calculated
//   'unmapped'     — no default and no override; nobody has linked this material
//   'unknown-code' — a code was entered that the standaard does not contain
//   'needs-factor' — linked, but the unit conversion is still missing
//   'no-kengetal'  — linked to an interventie the standaard cannot quantify
//
// Only 'ok' produces a figure. The other four must render as unknown, never as
// zero — controle.zonderKengetal is explicit that a missing kengetal "mag niet
// als nul worden gelezen".
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

import { defaultForMaterial } from './materialInterventionDefaults'
import isItemCountUnit from './isItemCountUnit'

/**
 * @typedef {import('../meetstandaard/adaptInterventieDocument').AdaptedInterventie} AdaptedInterventie
 *
 * @typedef {object} ResolvedMaterialIntervention
 * @property {'ok'|'unmapped'|'unknown-code'|'needs-factor'|'no-kengetal'} status
 * @property {string|null} code
 * @property {number|null} unitsPerItem - Registration units per registered item
 * @property {AdaptedInterventie|null} intervention
 */

/** @type {ResolvedMaterialIntervention} */
const UNMAPPED = { status: 'unmapped', code: null, unitsPerItem: null, intervention: null }

/**
 * A factor is only meaningful when it is a positive, finite number. An empty
 * string, 0 or a stray '-' means "not filled in", not "zero units".
 *
 * @param {*} value
 * @returns {number|null}
 */
const positiveNumberOr = (value) => {
    if (value === '' || value === null || value === undefined) return null
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * @param {{Title?: string, InterventionCode?: string, UnitsPerItem?: number|string}} material
 * @param {{byCode: Object<string, AdaptedInterventie>}} library - From adaptInterventieDocument
 * @returns {ResolvedMaterialIntervention}
 */
const resolveMaterialIntervention = (material, library) => {
    if (!material || !library?.byCode) return UNMAPPED

    const fallback = defaultForMaterial(material.Title)
    const code = material.InterventionCode || fallback?.code || null
    if (!code) return UNMAPPED

    const intervention = library.byCode[code] || null
    if (!intervention) {
        // Keep the code so the UI can show what was typed.
        return { status: 'unknown-code', code, unitsPerItem: null, intervention: null }
    }

    if (intervention.status === 'no-kengetal') {
        // Deliberately keeps the intervention: the detail view still wants to
        // name it and show why the standaard has no number for it.
        return { status: 'no-kengetal', code, unitsPerItem: null, intervention }
    }

    // Per-bewoner and per-woning interventies are scaled downstream by the
    // resident count or counted once per household. A unit conversion would be
    // meaningless, so an override factor is ignored rather than silently
    // multiplying a per-dwelling effect.
    if (intervention.scope !== 'perUnit') {
        return { status: 'ok', code, unitsPerItem: 1, intervention }
    }

    // An override of 0 or '' falls through to the default rather than being
    // read as "zero units", which would silently zero the material out.
    // Telt de maatregel het ding zelf ('per lamp', 'per stuk'), dan is één
    // geregistreerd product één eenheid. Het scherm vraagt hier ook niets en
    // zegt dat het materiaal meetelt; dezelfde regel uit hetzelfde bestand.
    const unitsPerItem = positiveNumberOr(material.UnitsPerItem)
        ?? positiveNumberOr(fallback?.unitsPerItem)
        ?? (isItemCountUnit(intervention.unit) ? 1 : null)

    if (unitsPerItem === null) {
        // Defaulting to 1 here is the tempting bug: it would bill one koker of
        // kit as one metre of kier and look entirely plausible.
        return { status: 'needs-factor', code, unitsPerItem: null, intervention }
    }

    return { status: 'ok', code, unitsPerItem, intervention }
}

export default resolveMaterialIntervention
